import React, { useState, useEffect, useContext } from "react"
import { useHistory } from "react-router-dom"
import { AuthContext } from "../../utils/providers/auth"
import { db } from "../../utils/firebase"
import firebase from "firebase"
import "./staff.scss"

/**
 * UI components
 */
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Table from "../../components/structure/table/table"
import Navigation from "../../components/ui/navigation/navigation"
import Enquiry from "../../components/enquiry/enquiry"
import Select from "../../components/ui/select/select"
import {
	readStoredSort,
	writeStoredSort,
	sortByCreatedDesc,
	getQueueSortValue,
	sortItems,
	nextSortState,
} from "../../utils/enquiries/sort"

const QUEUE_STATUSES = ["FREEPHONE", "HOT", "BEING_PROCESSED"]
const CUSTOM_TAB_PREFIX = "CUSTOM_TAB:"
const QUEUE_SORT_STORAGE_KEY = "staff.queueSort"

const queueLabel = {
	BEING_PROCESSED: "Processing",
	HOT: "Hot",
	FREEPHONE: "Freephone",
}

/**
 * Functional component to return the enquiry tables
 */
function StaffEnquiries() {
	const [agents, setAgents] = useState({})
	const [agent, setAgent] = useState({})
	const [activeTab, setActiveTab] = useState("BEING_PROCESSED")
	const [customTabs, setCustomTabs] = useState([])
	const [allEnquiries, setAllEnquiries] = useState([])
	const [clientsById, setClientsById] = useState({})
	const [loadingEnquiries, setLoadingEnquiries] = useState(false)
	const [queueSort, setQueueSort] = useState(() => readStoredSort(QUEUE_SORT_STORAGE_KEY))

	useEffect(() => {
		writeStoredSort(QUEUE_SORT_STORAGE_KEY, queueSort)
	}, [queueSort])

	// Pull the current user's object in from the auth context
	const { user } = useContext(AuthContext)
	const history = useHistory()
	const activeCustomTabID = activeTab.startsWith(CUSTOM_TAB_PREFIX) ? activeTab.replace(CUSTOM_TAB_PREFIX, "") : ""
	const activeCustomTab = customTabs.find((tab) => tab.id === activeCustomTabID)

	/**
	 * On load for the context
	 */
	useEffect(() => {
		/**
		 * Pull a list of agents from the use list
		 */
		db.collection("users")
			.orderBy("first_name")
			.where("show_for.assignable", "==", true)
			.get()
			.then((agentDocs) => {
				let agentsArr = []
				/**
				 * Loop through the agent documents found
				 */
				agentDocs.forEach((agentDoc) => {
					/**
					 * Deconstruct the agent details
					 */
					const { first_name, last_name } = agentDoc.data()
					/**
					 * Add them into the array
					 */
					agentsArr[agentDoc.id] = `${first_name} ${last_name}`
				})
				/**
				 * Push the user into the state
				 */
				setAgents(agentsArr)
			})
	}, [])

	/**
	 * Load the selected agent's saved enquiry tag tabs.
	 */
	useEffect(() => {
		if (!agent.option) {
			setCustomTabs([])
			return
		}

		const unsubscribeUser = db.doc(`users/${agent.option}`).onSnapshot((doc) => {
			if (!doc.exists) {
				setCustomTabs([])
				return
			}
			const userData = doc.data()
			const tabs = (userData.enquiry_tabs || []).sort((a, b) => (a.order || 0) - (b.order || 0))
			setCustomTabs(tabs)
		})

		return () => unsubscribeUser()
	}, [agent.option])

	/**
	 * Keep the selected agent's open queue enquiries in memory, then derive
	 * status and tag tabs locally to mirror the user enquiries page.
	 */
	useEffect(() => {
		if (!agent.option) {
			setAllEnquiries([])
			setClientsById({})
			setLoadingEnquiries(false)
			return
		}

		setLoadingEnquiries(true)
		setAllEnquiries([])
		setClientsById({})

		const unsubscribers = QUEUE_STATUSES.map((status) =>
			db
				.collection("enquiries")
				.where("agent", "==", agent.option)
				.where("status", "==", status)
				.onSnapshot((enquiriesSnap) => {
					const nextEnquiries = enquiriesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
					setAllEnquiries((currentEnquiries) => {
						const otherStatuses = currentEnquiries.filter((enquiry) => enquiry.status !== status)
						return [...otherStatuses, ...nextEnquiries].sort(sortByCreatedDesc)
					})
					setLoadingEnquiries(false)
				}, (err) => {
					console.error(`Unable to load ${status} enquiries`, err)
					setAllEnquiries((currentEnquiries) => currentEnquiries.filter((enquiry) => enquiry.status !== status))
					setLoadingEnquiries(false)
				})
		)

		return () => unsubscribers.forEach((unsubscribe) => unsubscribe())
	}, [agent.option])

	/**
	 * Preload client records for visible open enquiries so rows stay aligned
	 * with the user enquiries page and avoid cell-by-cell hydration.
	 */
	useEffect(() => {
		const missingClientIds = Array.from(
			new Set(
				allEnquiries
					.map((enquiry) => enquiry.client)
					.filter((clientId) => clientId && !clientsById[clientId])
			)
		)
		if (missingClientIds.length === 0) return

		let isMounted = true
		const loadClients = async () => {
			const clientData = {}
			const batchSize = 10
			for (let i = 0; i < missingClientIds.length; i += batchSize) {
				const batch = missingClientIds.slice(i, i + batchSize)
				const clientDocs = await db
					.collection("clients")
					.where(firebase.firestore.FieldPath.documentId(), "in", batch)
					.get()
				clientDocs.forEach((doc) => {
					clientData[doc.id] = doc.data()
				})
			}
			if (!isMounted) return
			setClientsById((currentClients) => ({
				...currentClients,
				...clientData,
			}))
		}

		loadClients().catch((err) => console.error("Unable to load enquiry clients", err))
		return () => {
			isMounted = false
		}
	}, [allEnquiries, clientsById])

	useEffect(() => {
		if (!activeCustomTabID) return
		if (customTabs.length > 0 && !customTabs.some((tab) => tab.id === activeCustomTabID)) {
			setActiveTab("BEING_PROCESSED")
		}
	}, [activeCustomTabID, customTabs])

	const handleAgentSelect = (option) => {
		setAgent(option)
		setActiveTab("BEING_PROCESSED")
	}

	const customNavigation = customTabs.map((tab) => ({
		id: `${CUSTOM_TAB_PREFIX}${tab.id}`,
		label: tab.label,
		count: allEnquiries.filter((enquiry) => {
			const tags = enquiry.tags || []
			return (tab.tags || []).every((tag) => tags.includes(tag))
		}).length,
	}))

	const navigation = [
		{
			id: "STATUS_LABEL",
			label: "Status",
			divider: true,
		},
		{
			id: "BEING_PROCESSED",
			label: "Processing",
			count: allEnquiries.filter((enquiry) => enquiry.status === "BEING_PROCESSED").length,
		},
		{
			id: "HOT",
			label: "Hot",
			count: allEnquiries.filter((enquiry) => enquiry.status === "HOT").length,
		},
		{
			id: "FREEPHONE",
			label: "Freephone",
			count: allEnquiries.filter((enquiry) => enquiry.status === "FREEPHONE").length,
		},
		...(customNavigation.length > 0 ? [{ id: "CUSTOM_TAGS_LABEL", label: "Tags", divider: true, spaced: true }] : []),
		...customNavigation,
	]

	const unsortedEnquiries = activeCustomTab
		? allEnquiries.filter((enquiry) => {
			const tags = enquiry.tags || []
			return (activeCustomTab.tags || []).every((tag) => tags.includes(tag))
		})
		: allEnquiries.filter((enquiry) => enquiry.status === activeTab)
	const enquiries = sortItems(
		unsortedEnquiries,
		queueSort,
		(enquiry, key) => getQueueSortValue(enquiry, key, clientsById)
	)
	const queueHeadings = [
		{ key: "site", label: "Site" },
		{ key: "name", label: "Name" },
		{ key: "email", label: "Email" },
		{ key: "telephone", label: "Telephone" },
		{ key: "follow_up", label: "Follow up" },
		{ key: "location", label: "Location/Event" },
		{ key: "date_from", label: "Date from" },
		{ key: "group_size", label: "Group size" },
		{ key: "quoted", label: "Quoted" },
		{ key: "age", label: "Age" },
	].map((heading) => ({
		...heading,
		sortable: true,
		active: queueSort.key === heading.key,
		direction: queueSort.key === heading.key ? queueSort.direction : "",
		onClick: () => setQueueSort((currentSort) => nextSortState(currentSort, heading.key)),
	}))

	const emptyQueueMessage = !agent.option
		? "Choose an agent to view their enquiries."
		: activeCustomTab
			? `No enquiries match every tag on "${activeCustomTab.label}".`
			: `There are no enquiries in ${queueLabel[activeTab] || "this tab"}.`

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Agent enquiries</h1>

				{/* Dropdown to choose a sales agent to view */}
				<Select
					placeholder="Choose agent"
					value={agent.value}
					selected={agent.option}
					activeOnHover={true}
					onSelect={handleAgentSelect}
					options={agents}
				/>
			</Title>

			<Navigation
				links={navigation}
				active={activeTab}
				onClick={(tabID) => setActiveTab(tabID)}
			/>

			{activeCustomTab && (
				<div className="enquiry-active-tag-preview">
					<span>Showing enquiries tagged with</span>
					<div className="enquiry-active-tag-list">
						{(activeCustomTab.tags || []).map((tag) => (
							<span
								key={tag}
								className="enquiry-active-tag">
								{tag}
							</span>
						))}
					</div>
				</div>
			)}

			{/* Print the various results into the table */}
			<Table
				className="enquiries-table"
				headings={queueHeadings}>
				{loadingEnquiries && (
					<tr>
						<td colSpan={10}>
							<small>Loading enquiries...</small>
						</td>
					</tr>
				)}

				{!loadingEnquiries && enquiries.map((enquiry) => (
					<Enquiry
						key={enquiry.id}
						id={enquiry.id}
						details={enquiry}
						maskDetails={user?.mask_details}
						clientData={clientsById[enquiry.client]}
						showEnquiryWindow={(enquiryID) => history.push(`/enquiry/${enquiryID}`)}
					/>
				))}

				{!loadingEnquiries && enquiries.length === 0 && (
					<tr>
						<td colSpan={10}>
							<small>{emptyQueueMessage}</small>
						</td>
					</tr>
				)}
			</Table>
		</Tile>
	)
}

export default StaffEnquiries
